import React, { useEffect, useState, useCallback, useRef } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { Clock, ChevronLeft, ChevronRight, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";

const SLUG_TO_KEY = {
  "canada-citizenship": "canada_citizenship",
  "alberta-class-7":    "alberta_class7",
  "ontario-g1":         "ontario_g1",
};

export default function PracticePlayer() {
  const { slug, attemptId } = useParams();
  const nav = useNavigate();
  const [attempt, setAttempt] = useState(null);
  const [answers, setAnswers] = useState({});
  const [idx, setIdx] = useState(0);
  const [elapsed, setElapsed] = useState(0);
  const [submitting, setSubmitting] = useState(false);
  const startedRef = useRef(Date.now());

  useEffect(() => {
    // Resume the attempt: load question payload (server preserves order on resume).
    api.get(`/api/practice/attempts/${attemptId}/resume`).then(({ data }) => {
      setAttempt(data);
      startedRef.current = Date.now() - (data.elapsed_sec || 0) * 1000;
    }).catch((e) => {
      toast.error(e?.response?.data?.detail || "Could not resume attempt");
      nav(`/practice/${slug}`);
    });
  }, [attemptId, nav, slug]);

  useEffect(() => {
    const t = setInterval(() => setElapsed(Math.floor((Date.now() - startedRef.current) / 1000)), 1000);
    return () => clearInterval(t);
  }, []);

  const total = attempt?.questions?.length || 0;
  const q = attempt?.questions?.[idx];

  const choose = (qid, oi) => setAnswers((a) => ({ ...a, [qid]: oi }));

  const submit = useCallback(async () => {
    if (submitting) return;
    setSubmitting(true);
    try {
      const { data } = await api.post(`/api/practice/attempts/${attemptId}/submit`, {
        answers, time_taken_sec: elapsed,
      });
      nav(`/practice/${slug}/results/${attemptId}`, { state: data });
    } catch (e) {
      toast.error("Could not submit — try again");
    } finally { setSubmitting(false); }
  }, [submitting, attemptId, answers, elapsed, nav, slug]);

  if (!attempt) return <div className="p-12 text-center text-[#4B5563]" data-testid="player-loading">Loading test…</div>;

  const answered = Object.keys(answers).length;
  const pct = total ? Math.round(100 * answered / total) : 0;
  const mins = Math.floor(elapsed / 60), secs = elapsed % 60;
  const isRTL = attempt.lang === "ur";

  return (
    <div className="max-w-3xl mx-auto px-6 py-8" data-testid="practice-player"
      dir={isRTL ? "rtl" : "ltr"}>
      <div className="flex items-center justify-between mb-4">
        <div>
          <div className="text-xs uppercase tracking-widest text-[#D92D20] font-bold">Practice Test in Progress</div>
          <div className="font-display font-bold text-lg">Question {idx + 1} of {total}</div>
        </div>
        <div className="text-right">
          <div className="inline-flex items-center gap-1 text-sm font-bold text-[#064E3B]" data-testid="player-timer">
            <Clock className="w-4 h-4" /> {String(mins).padStart(2,"0")}:{String(secs).padStart(2,"0")}
          </div>
          <div className="text-xs text-[#4B5563]">Answered {answered}/{total}</div>
        </div>
      </div>
      <div className="h-2 bg-[#F3F4F6] rounded-full overflow-hidden mb-6">
        <div className="h-full bg-[#D92D20] transition-all" style={{ width: `${pct}%` }} />
      </div>

      <div className="bg-white border border-[#E5E7EB] rounded-2xl p-6">
        <div className="text-xs uppercase tracking-widest text-[#4B5563] font-bold">{q.category} · {q.difficulty}</div>
        <div className="font-display font-bold text-xl mt-2" data-testid="player-question">{q.question}</div>
        <div className="mt-5 space-y-2">
          {q.options.map((o, i) => {
            const selected = answers[q.id] === i;
            return (
              <button key={`${q.id}-${i}`} data-testid={`player-option-${i}`}
                onClick={() => choose(q.id, i)}
                className={`w-full text-left text-sm px-4 py-3 rounded-xl border transition-all ${
                  selected ? "border-[#D92D20] bg-[#FEF2F2] font-semibold" : "border-[#E5E7EB] hover:border-[#9CA3AF]"
                }`}>
                <span className="font-bold mr-2">{String.fromCharCode(65 + i)}.</span> {o}
                {selected && <CheckCircle2 className="w-4 h-4 inline ml-2 text-[#D92D20]" />}
              </button>
            );
          })}
        </div>
      </div>

      <div className="mt-5 flex justify-between gap-3">
        <button data-testid="player-prev" onClick={() => setIdx(Math.max(0, idx - 1))} disabled={idx === 0}
          className="btn-outline inline-flex items-center gap-1 disabled:opacity-40">
          <ChevronLeft className="w-4 h-4" /> Previous
        </button>
        {idx < total - 1 ? (
          <button data-testid="player-next" onClick={() => setIdx(idx + 1)}
            className="btn-primary inline-flex items-center gap-1">
            Next <ChevronRight className="w-4 h-4" />
          </button>
        ) : (
          <button data-testid="player-submit" onClick={submit} disabled={submitting}
            className="btn-primary disabled:opacity-50">
            {submitting ? "Submitting…" : "Submit test"}
          </button>
        )}
      </div>

      <div className="mt-4 text-xs text-center text-[#4B5563]">
        You can navigate freely between questions before submitting.
      </div>
    </div>
  );
}
